import { MessageCircle, Phone, Mail, MapPin, Clock } from "lucide-react";
import { SITE_CONFIG, getWhatsAppLink, getTelLink, getMailLink } from "../lib/config";

const CARDS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: SITE_CONFIG.phone,
    href: getWhatsAppLink(),
    color: "from-[#25D366] to-[#128C7E]",
    testId: "contatti-whatsapp",
    external: true,
    description: "Risposta rapida",
  },
  {
    icon: Phone,
    label: "Telefono",
    value: SITE_CONFIG.phone,
    href: getTelLink(),
    color: "from-emerald-500 to-teal-500",
    testId: "contatti-tel",
    description: "Chiamata diretta",
  },
  {
    icon: Mail,
    label: "Email",
    value: SITE_CONFIG.email,
    href: getMailLink("Richiesta info dal sito"),
    color: "from-sky-500 to-emerald-500",
    testId: "contatti-email",
    description: "Per preventivi articolati",
  },
];

export default function ContattiPage() {
  return (
    <div data-testid="contatti-page" className="min-h-screen bg-slate-950 text-white">
      <section className="relative pt-40 pb-16">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 70% 30%, rgba(16,185,129,0.18), transparent 50%), radial-gradient(circle at 30% 80%, rgba(20,184,166,0.15), transparent 50%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="text-xs font-semibold tracking-[0.25em] text-emerald-400 uppercase mb-4">
            Contatti
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Come <span className="text-emerald-400">raggiungerci</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl">
            Scrivi su WhatsApp per una risposta immediata, oppure utilizza email e telefono.
            Operiamo in tutta Italia con sede a {SITE_CONFIG.city}.
          </p>
        </div>
      </section>

      <section data-testid="contact-cards-section" className="pb-16">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-3 gap-5">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <a
                key={i}
                href={c.href}
                target={c.external ? "_blank" : undefined}
                rel={c.external ? "noopener noreferrer" : undefined}
                data-testid={c.testId}
                className="group p-7 rounded-3xl border border-white/5 bg-white/[0.02] hover:border-emerald-400/40 hover:bg-white/[0.04] transition-all"
              >
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform`}
                >
                  <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                </div>
                <div className="text-xs font-semibold tracking-widest text-emerald-300 uppercase mb-2">
                  {c.label} — {c.description}
                </div>
                <div className="text-lg font-bold text-white break-words">{c.value}</div>
              </a>
            );
          })}
        </div>
      </section>

      <section data-testid="info-section" className="pb-24">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-6">
          <div className="p-7 rounded-3xl border border-white/5 bg-white/[0.02]">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-5">
              <MapPin className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Sede & Operatività</h3>
            <p className="text-base text-slate-300 mb-2">
              <strong className="text-white">Sede legale:</strong> {SITE_CONFIG.city}, {SITE_CONFIG.region}
            </p>
            <p className="text-base text-slate-300">
              <strong className="text-white">Operiamo in:</strong> tutta Italia, da Nord a Sud,
              isole comprese.
            </p>
          </div>

          <div className="p-7 rounded-3xl border border-white/5 bg-white/[0.02]">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-5">
              <Clock className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Orari di apertura</h3>
            <ul className="text-base text-slate-300 space-y-1.5">
              <li className="flex justify-between"><span>Lunedì — Venerdì</span><span className="text-emerald-300 font-semibold">09:00 — 19:00</span></li>
              <li className="flex justify-between"><span>Sabato</span><span className="text-emerald-300 font-semibold">09:00 — 13:00</span></li>
              <li className="flex justify-between"><span>Domenica</span><span className="text-slate-500">Chiuso</span></li>
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="big-wa-section" className="pb-32">
        <div className="max-w-4xl mx-auto px-5">
          <a
            href={getWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="big-whatsapp-cta"
            className="block p-10 lg:p-14 rounded-3xl bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-emerald-500/5 border border-emerald-400/30 hover:border-emerald-400/60 transition-all text-center"
          >
            <div className="w-20 h-20 rounded-full bg-[#25D366] mx-auto flex items-center justify-center mb-6 shadow-2xl shadow-emerald-500/30">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3 tracking-tight">
              Scrivimi su WhatsApp
            </h2>
            <p className="text-base text-slate-300 mb-6">
              Il modo più veloce per parlare con me. Risposta in poche ore nei giorni lavorativi.
            </p>
            <div className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#25D366] text-white font-bold hover:scale-105 transition">
              {SITE_CONFIG.phone}
            </div>
          </a>
        </div>
      </section>
    </div>
  );
}
